import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Checkbox,
  CheckboxControl,
  CheckboxLabel,
} from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PROPOSAL_STATE } from "@/domains/application/constant/status";
import type { JobApplicationStatus } from "@/domains/company/company/api/types";
import type { JobOfferV2WithCompanyId } from "@/domains/job-offer/types";
import { cn } from "@/libs/cn";
import { useReportCandidateCount } from "../api/report-audience";
import { REPORT_APPLICANT_CAP } from "../constants";
import {
  DEFAULT_REPORT_FILTERS,
  isUnfiltered,
  type ReportFilters,
} from "../types";

/**
 * Gli stati offerti per le candidature spontanee.
 *
 * Volutamente più ampio dei pill della pagina ATS, che escludono `selected` e
 * `retired`: quei candidati esistono nei dati, e un report che non può parlarne
 * sarebbe monco proprio sulle candidature chiuse con successo.
 */
const APPLICATION_STATUSES: JobApplicationStatus[] = [
  "pending",
  "viewed",
  "approved",
  "in_negotiation",
  "rejected",
  "selected",
  "retired",
];

/** Stati che l'ATS mostra come pill, con il loro nome. */
const ATS_PILL_STATUSES: ReadonlySet<string> = new Set([
  "pending",
  "viewed",
  "approved",
  "in_negotiation",
  "rejected",
]);

export type ReportFiltersDialogProps = {
  /** L'offerta per cui generare; `null` tiene il dialog chiuso. */
  offer: JobOfferV2WithCompanyId | null;
  prefill?: Partial<ReportFilters>;
  onConfirm: (filters: ReportFilters, candidateCount: number) => void;
  onDismiss: () => void;
};

/**
 * Scelta della platea prima di generare il report.
 *
 * Rimpiazza il dialogo over-cap: quello avvisava che sarebbero stati presi i
 * primi N senza dare modo di intervenire, mentre qui il cap si applica **dopo**
 * il filtro, quindi restringere la selezione è la via per scegliere quali.
 *
 * Montato nel provider e non nelle route: il precedente `ReportOverCapDialog`
 * era montato due volte, una per pagina, e ogni superficie nuova avrebbe
 * ereditato quella duplicazione.
 */
export function ReportFiltersDialog(props: ReportFiltersDialogProps) {
  const { t } = useI18n();
  const key = "profile.company.ats.report.filters";

  const [filters, setFilters] = createSignal<ReportFilters>({
    ...DEFAULT_REPORT_FILTERS,
  });

  /**
   * Ri-semina a ogni apertura.
   *
   * Il dialog e' montato in permanenza nel provider e `open` ne commuta solo la
   * visibilita', quindi il valore iniziale del signal verrebbe calcolato una
   * volta sola all'avvio dell'app — con `prefill` ancora `undefined`. Senza
   * questo, il prefill non si applicherebbe mai e le scelte fatte su un'offerta
   * resterebbero appiccicate alla successiva.
   */
  createEffect(() => {
    if (!props.offer) return;
    setFilters({ ...DEFAULT_REPORT_FILTERS, ...props.prefill });
  });

  const count = useReportCandidateCount(
    () => props.offer?.id ?? null,
    filters,
    () => props.offer !== null,
  );

  const total = () => count.data?.count;
  const overCap = () => (total() ?? 0) > REPORT_APPLICANT_CAP;
  const canConfirm = () => !count.isPending && (total() ?? 0) > 0;

  /**
   * Cambiare tipologia azzera la selezione dell'altra tassonomia.
   *
   * Non è cosmetico: mandare `statuses` insieme a `initiator: "company"` è un
   * 400, perché le due colonne descrivono processi diversi e il loro AND è
   * l'insieme vuoto.
   */
  const setInitiator = (initiator: ReportFilters["initiator"]) =>
    setFilters((current) => ({
      ...current,
      initiator,
      statuses: [],
      proposalStates: [],
    }));

  const toggle = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const statusOptions = createMemo(() =>
    filters().initiator === "company"
      ? PROPOSAL_STATE.map((state) => ({
          value: state as string,
          label: t(`profile.tabs.proposals.status_tabs.${state}`) as string,
        }))
      : APPLICATION_STATUSES.map((status) => ({
          value: status as string,
          // Le stesse parole dei pill dell'ATS che il recruiter ha davanti
          // («Nuove», «Viste», «Non idonee»). «Selezionato» e «Chiusa» non hanno
          // un pill: ripiegano sulle etichette delle tab.
          label: (ATS_PILL_STATUSES.has(status)
            ? t(`profile.company.ats.detail.status_pills.${status}`)
            : t(`profile.tabs.applications.status_tabs.${status}`)) as string,
        })),
  );

  const isChecked = (value: string) =>
    filters().initiator === "company"
      ? filters().proposalStates.includes(
          value as ReportFilters["proposalStates"][number],
        )
      : filters().statuses.includes(value as JobApplicationStatus);

  const toggleStatus = (value: string) =>
    setFilters((current) =>
      current.initiator === "company"
        ? {
            ...current,
            proposalStates: toggle(
              current.proposalStates,
              value as ReportFilters["proposalStates"][number],
            ),
          }
        : {
            ...current,
            statuses: toggle(current.statuses, value as JobApplicationStatus),
          },
    );

  return (
    <Dialog
      open={props.offer !== null}
      onOpenChange={(open) => !open && props.onDismiss()}
    >
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${key}.title`) as string}</DialogTitle>
          <DialogDescription>
            {t(`${key}.description`) as string}
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-5">
          <div class="space-y-2">
            <p class="text-sm font-medium">
              {t(`${key}.type_label`) as string}
            </p>
            <div class="inline-flex rounded-md border p-0.5">
              <For
                each={
                  [
                    ["freelancer", `${key}.type_freelancer`],
                    ["company", `${key}.type_company`],
                  ] as const
                }
              >
                {([value, label]) => (
                  <button
                    type="button"
                    class={cn(
                      "rounded px-3 py-1.5 text-sm transition-colors",
                      filters().initiator === value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setInitiator(value)}
                  >
                    {t(label) as string}
                  </button>
                )}
              </For>
            </div>
          </div>

          <div class="space-y-2">
            <p class="text-sm font-medium">
              {t(`${key}.status_label`) as string}
            </p>
            <div class="grid grid-cols-2 gap-2">
              <For each={statusOptions()}>
                {(option) => (
                  <Checkbox
                    class="flex items-center gap-2"
                    checked={isChecked(option.value)}
                    onChange={() => toggleStatus(option.value)}
                  >
                    <CheckboxControl />
                    <CheckboxLabel class="text-sm">
                      {option.label}
                    </CheckboxLabel>
                  </Checkbox>
                )}
              </For>
            </div>
          </div>

          {/*
            Separato dagli stati, con la spiegazione accanto: «contattato» è un
            predicato ortogonale (`contacted_at IS NOT NULL`), non uno stato.
            Messo in fila con gli altri verrebbe letto come alternativo, che è
            esattamente come lo legge male la pagina ATS oggi.
          */}
          <div class="space-y-1 border-t pt-4">
            <Checkbox
              class="flex items-center gap-2"
              checked={filters().contactedOnly}
              onChange={(checked) =>
                setFilters((current) => ({
                  ...current,
                  contactedOnly: checked,
                }))
              }
            >
              <CheckboxControl />
              <CheckboxLabel class="text-sm">
                {t(`${key}.contacted_label`) as string}
              </CheckboxLabel>
            </Checkbox>
            <p class="pl-6 text-xs text-muted-foreground">
              {t(`${key}.contacted_hint`) as string}
            </p>
          </div>

          {/* Le note non si stampano: il backend le passa, ripulite, al modello
              che scrive la valutazione. Non c'è nulla da scegliere qui. */}
          <p class="border-t pt-4 text-xs text-muted-foreground">
            {t(`${key}.notes_hint`) as string}
          </p>

          <Show when={isUnfiltered(filters())}>
            <Alert variant="info">
              <AlertTitle>{t(`${key}.unfiltered_title`) as string}</AlertTitle>
              <AlertDescription>
                {t(`${key}.unfiltered_description`) as string}
              </AlertDescription>
            </Alert>
          </Show>

          <Show when={overCap()}>
            <Alert variant="warning">
              <AlertDescription>
                {
                  t(`${key}.over_cap`, {
                    max: REPORT_APPLICANT_CAP,
                  }) as string
                }
              </AlertDescription>
            </Alert>
          </Show>
        </div>

        <DialogFooter class="items-center justify-between gap-3 sm:justify-between">
          <p
            class={cn(
              "text-sm",
              total() === 0 ? "text-destructive" : "text-muted-foreground",
            )}
          >
            <Show when={!count.isPending} fallback={t(`${key}.count_loading`)}>
              <Show
                when={(total() ?? 0) > 0}
                fallback={t(`${key}.count_empty`) as string}
              >
                {
                  t(total() === 1 ? `${key}.count_one` : `${key}.count_other`, {
                    count: total() ?? 0,
                  }) as string
                }
              </Show>
            </Show>
          </p>
          <div class="flex gap-2">
            <Button variant="ghost" onClick={props.onDismiss}>
              {t(`${key}.cancel`) as string}
            </Button>
            <Button
              disabled={!canConfirm()}
              onClick={() => props.onConfirm(filters(), total() ?? 0)}
            >
              {t(`${key}.confirm`) as string}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
